import React, { FC } from 'react';
import moment, { Moment } from 'moment';
import { DateSelect, SingleCalendar } from '@shared/UI';
import { useNotify } from '@features/Notify';
import { generateEDoc } from '../../api';
import { getApiError, IServiceEdocTypes } from '@shared/api';
import { useModal } from '@features/Modal/lib/ModalProvider';
import './EDocsModal.scss';

interface IEDocsModalProps {
    file: string;
    clearingAccount: string;
}

export const EDocsModal: FC<IEDocsModalProps> = ({ file, clearingAccount }) => {
    const notify = useNotify();
    const { removeModal } = useModal();

    const validateDate = (date: string | Moment): boolean => {
        const today = moment();
        return moment(date).isBefore(today);
    };

    const openReport = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const onSelectDate = (period: 'day' | 'month' | 'year', date: string | Moment, type: IServiceEdocTypes) => {
        generateEDoc({
            accountNumber: clearingAccount,
            types: [type],
            beginDate: moment(date)
                .startOf(period)
                .format(period === 'day' ? 'YYYY-MM-DD' : undefined),
            endDate: moment(date)
                .endOf(period)
                .format(period === 'day' ? 'YYYY-MM-DD' : undefined),
        })
            .then((result) => {
                if (!result.length) {
                    return notify.error('No data in the selected date');
                }
                openReport(result[0].url);
            })
            .catch((error) => {
                notify.error(getApiError(error));
            })
            .finally(removeModal);
    };

    const fileActions = {
        'Trade Confirmations': () => (
            <DateSelect
                placeholder='Select Date'
                dropdownPlacement='bottom-left'
                format={'MMM, Do, YYYY'}
                onDateSelect={(date) => {
                    const isBefore = validateDate(moment(date));
                    if (!isBefore || !date) {
                        return notify.info('Select past date');
                    }
                    onSelectDate('day', date, 'TradeConfirmation');
                }}
            />
        ),
        'Account Statements': () => (
            <SingleCalendar
                initialMode={'month'}
                onCustomMonthSelect={(month, year) => {
                    const date = moment().year(year).month(month).startOf('month');
                    const isBefore = validateDate(moment(date));
                    if (!isBefore) {
                        return notify.info('Select past date');
                    }
                    onSelectDate('month', date, 'AccountStatement');
                }}
                onDateSelect={() => {}}
            />
        ),
        '1099 Composite': () => (
            <SingleCalendar
                initialMode={'year'}
                hideMonth
                hideYear
                onCustomYearSelect={(year) => {
                    const date = moment().year(year).startOf('year');
                    const isBefore = validateDate(date);
                    if (!isBefore) {
                        return notify.info('Select past date');
                    }
                    onSelectDate('year', date, 'TaxForm');
                }}
                onDateSelect={() => {}}
            />
        ),
        '1099-B': () => (
            <SingleCalendar
                initialMode={'year'}
                hideMonth
                hideYear
                onCustomYearSelect={(year) => {
                    const date = moment().year(year).startOf('year');
                    const isBefore = validateDate(date);
                    if (!isBefore) {
                        return notify.info('Select past date');
                    }
                    onSelectDate('year', date, 'TaxFormB');
                }}
                onDateSelect={() => {}}
            />
        ),
        '1099-DIV': () => (
            <SingleCalendar
                initialMode={'year'}
                hideMonth
                hideYear
                onCustomYearSelect={(year) => {
                    const date = moment().year(year).startOf('year');
                    const isBefore = validateDate(date);
                    if (!isBefore) {
                        return notify.info('Select past date');
                    }
                    onSelectDate('year', date, 'TaxFormDIV');
                }}
                onDateSelect={() => {}}
            />
        ),
    };

    const Picker = fileActions[file as keyof typeof fileActions];

    return (
        <div className='e-docs-modal'>
            <Picker />
        </div>
    );
};
